import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { apiService } from '../services/api';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
  nickname?: string;
}

interface AccessControlModalProps {
  open: boolean;
  onClose: () => void;
  onUserCreated: (newUser?: any) => void;
  editingUser?: any;
}

const AccessControlModal: React.FC<AccessControlModalProps> = ({
  open,
  onClose,
  onUserCreated,
  editingUser,
}) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    email: '',
    password: '',
    role: 'employee',
    isActive: true,
  });

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadEmployees();
      if (editingUser) {
        setFormData({
          employeeId: editingUser.employeeId || '',
          email: editingUser.email || '',
          password: '',
          role: editingUser.role || 'employee',
          isActive: editingUser.isActive ?? true,
        });
      } else {
        resetForm();
      }
    }
  }, [open, editingUser]);

  const resetForm = () => {
    setFormData({
      employeeId: '',
      email: '',
      password: '',
      role: 'employee',
      isActive: true,
    });
  };

  const loadEmployees = async () => {
    try {
      setDataLoading(true);
      const employeesData = await apiService.getEmployees();
      setEmployees(employeesData);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEmployeeSelect = (employee: Employee | null) => {
    if (employee) {
      setFormData(prev => ({
        ...prev,
        employeeId: employee.id,
        email: employee.email,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        employeeId: '',
        email: '',
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const userData = {
        ...formData,
        employeeId: formData.employeeId || undefined,
      };

      if (editingUser && editingUser.id) {
        // Update existing user
        await apiService.updateUser(editingUser.id, userData);
        onUserCreated();
        onClose();
        resetForm();
      } else {
        // Create new user
        const response = await apiService.createUser(userData);
        onUserCreated(response);
        onClose();
        resetForm();
      }
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || `Failed to ${editingUser?.id ? 'update' : 'create'} user account`);
    } finally {
      setLoading(false);
    }
  };


  const isFormValid = formData.email && formData.role;

  const roleOptions = [
    { value: 'hr_admin', label: 'HR Admin', description: 'Full system access' },
    { value: 'employee', label: 'Employee', description: 'Limited access to leave and payroll' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingUser?.id ? 'Edit User Account' : 'Create User Account'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!editingUser?.id && (
              <Alert severity="info" sx={{ mb: 2 }}>
                A secure password will be automatically generated for this user. You can view and copy the password from the Access Control table after creation by clicking the reset password button.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.nickname || ''} (${option.email})`}
                value={employees.find(e => e.id === formData.employeeId) || null}
                onChange={(event, newValue) => handleEmployeeSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Employee"
                    helperText="Link this account to an employee record"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                clearOnEscape
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                helperText="This will be used for login"
              />
            </Grid>


            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  label="Role"
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Box>
                        <Box fontWeight="bold">{role.label}</Box>
                        <Box fontSize="0.875rem" color="text.secondary">
                          {role.description}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : editingUser?.id ? 'Update User' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessControlModal;